import React, { useState } from 'react';
import { useAddProjectImagesMutation } from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';

interface ImageUploadFormProps {
  projectId: number;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

const ImageUploadForm: React.FC<ImageUploadFormProps> = ({ 
  projectId, 
  onSuccess, 
  onError 
}) => {
  const [addImages, { isLoading }] = useAddProjectImagesMutation();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
  };

  const handleFiles = (files: File[]) => {
    // Validate files
    const validFiles = files.filter(file => {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(`File ${file.name} is not a valid image file`);
        return false;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large (max 10MB)`);
        return false;
      }
      
      // Check file size (min 1KB)
      if (file.size < 1024) {
        toast.error(`File ${file.name} is too small (min 1KB)`);
        return false;
      }
      
      return true;
    });

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 10)); // Max 10 files
      toast.success(`${validFiles.length} file(s) added successfully`);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      handleFiles(files);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    // Check authentication token
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('No authentication token found. Please login first.');
      return;
    }

    try {
      console.log('Uploading images for project:', projectId);
      console.log('Files to upload:', selectedFiles);
      console.log('Token:', token.substring(0, 20) + '...');

      const result = await addImages({
        project_id: projectId,
        images: selectedFiles
      }).unwrap();

      // Safety check (though baseQuery now handles success: false as errors)
      if (result && result.success === false) {
        toast.error(result.message || 'Failed to upload images');
        onError?.(result);
        return;
      }

      toast.success('Images uploaded successfully!');
      setSelectedFiles([]);
      onSuccess?.();
    } catch (error: any) {
      console.error('Image upload error:', error);
      
      // Extract error message from various possible structures
      let errorMessage = 'Upload failed';
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      if (error?.status === 401 || error?.data?.status === 401) {
        toast.error('Authentication failed. Please login again.');
        // Clear invalid token
        localStorage.removeItem('diasporawise_admin_token');
        localStorage.removeItem('diasporawise_admin_user');
        // Redirect to login
        window.location.href = '/admin/login';
      } else if (error?.status === 400 || error?.data?.status === 400) {
        toast.error(errorMessage || 'Bad request. Please check your files and try again.');
      } else {
        toast.error(errorMessage);
      }
      
      onError?.(error);
    }
  };

  return (
    <div className="space-y-6">
      {/* File Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 hover:border-blue-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          id="image-upload"
        />
        <label htmlFor="image-upload" className="cursor-pointer">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="mt-2 text-sm text-gray-600">
            Click to upload images or drag and drop
          </p>
          <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each (Max 10 files)</p>
        </label>
      </div>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Selected Files ({selectedFiles.length}/10)</h4>
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={isLoading || selectedFiles.length === 0}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Upload Images</span>
            </>
          )}
        </button>
      </div>

      {/* Debug Info */}
      <div className="text-xs text-gray-500 space-y-1">
        <p>Project ID: {projectId}</p>
        <p>Token: {localStorage.getItem('diasporawise_admin_token') ? 'Present' : 'Missing'}</p>
        <p>Files: {selectedFiles.length}</p>
      </div>
    </div>
  );
};

export default ImageUploadForm;
